import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useModalConfirm } from "@ryde/redux";
import { ConfirmResult, IDropdownItem } from "@ryde/shared";

import { useDashboardViews } from "../../hooks/use-dashboard-views";
import { DashboardViewContext } from "./dashboard-view.context";

/**
 * Dashboard fork of the Rides `useViewDropdown`. Same shape/behavior as the
 * original, but the data seam is rewired from the grid atoms to the dashboard's
 * card-layout handlers (`use-dashboard-views`): apply/update/delete operate on
 * saved card layouts, not table state. Delete keeps a confirm dialog (the one
 * intentional deviation from Rides).
 */
export const useDashboardViewDropdown = () => {
  const { t } = useTranslation();
  const {
    isDropdownOpen: isOpen,
    setIsDropdownOpen: setIsOpen,
    openEditView,
  } = useContext(DashboardViewContext);
  const { confirmDialogResult } = useModalConfirm();

  const {
    views,
    activeViewId,
    defaultView,
    applyView,
    clearActiveView,
    updateView,
    deleteView,
    setDefaultView,
  } = useDashboardViews();

  const onViewDropdownChange = useCallback(
    (newValue: unknown | undefined): void => {
      if (!newValue) {
        clearActiveView();
        return;
      }

      const viewId = (newValue as IDropdownItem).value;
      applyView(Number(viewId));
      setIsOpen?.(false);
    },
    [applyView, clearActiveView, setIsOpen],
  );

  const handleDeleteView = useCallback(
    async (viewId: number) => {
      const result = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t("dashboard.dynamic.deleteViewConfirm", {
          defaultValue: "Delete this view?",
        }),
      });

      if (result !== ConfirmResult.OK) {
        return;
      }

      await deleteView(Number(viewId));
      setIsOpen?.(false);
    },
    [confirmDialogResult, deleteView, setIsOpen, t],
  );

  const handleUpdateView = useCallback(
    async (viewId: number) => {
      await updateView(Number(viewId));
      setIsOpen?.(false);
    },
    [setIsOpen, updateView],
  );

  const editViewHandler = useCallback(
    (viewId: number) => {
      openEditView(views.find(v => v.id === viewId));
    },
    [openEditView, views],
  );

  // The ★ is a direct one-click "set as default" toggle (clearer than Rides'
  // star-opens-dialog); rename / scope changes go through the ⋮ edit dialog.
  const setDefaultHandler = useCallback(
    (viewId: number) => {
      setDefaultView(Number(viewId));
    },
    [setDefaultView],
  );

  const handleToggle = useEvent(() => setIsOpen?.(prev => !prev));
  const handleClose = useEvent(() => setIsOpen?.(false));

  const mappedViews: IDropdownItem[] = useMemo(() => {
    if (!views?.length) {
      return [];
    }

    return views.map(view => ({ value: view.id || 0, label: view.name }));
  }, [views]);

  return {
    handleClose,
    handleDeleteView,
    handleToggle,
    handleUpdateView,
    onViewDropdownChange,
    editViewHandler,
    setDefaultHandler,
    isOpen,
    mappedViews,
    activeViewId,
    defaultViewId: defaultView?.id,
  };
};
